import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Alert,
  Badge,
  Card,
  Center,
  Grid,
  Group,
  Loader,
  Space,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconClock,
  IconDroplet,
  IconBattery,
  IconTemperature,
} from "@tabler/icons-react";
import PublicLayout from "../../components/layouts/PublicLayout";
import { fetchSharedViewAsync } from "../../actions/sharedUrl.actions";
import { SharedMeterDataType } from "../../types";
import { formatNumberWithCommas } from "../../utils/numbers";
import { formatDate } from "../../utils/date";
import SharedMeterDetails from "../../views/shared/SharedMeterDetails";

export default function SharedViewPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [meters, setMeters] = useState<SharedMeterDataType[]>([]);
  const [selectedMeter, setSelectedMeter] = useState<SharedMeterDataType | null>(null);

  useEffect(() => {
    if (token) {
      fetchSharedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSharedData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchSharedViewAsync(token!);

      if (response?.data) {
        setExpiresAt(response.data.expiresAt);
        setMeters(response.data.meters);
        if (response.data.meters.length === 1) {
          setSelectedMeter(response.data.meters[0]);
        }
      } else if (response?.response?.data?.message) {
        setError(response.response.data.message);
      } else {
        setError("Failed to load shared data");
      }
    } catch (err) {
      setError("An error occurred while loading the data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <Center h={400}>
          <Group>
            <Loader color="blue" type="bars" />
            <Text>Loading meter data...</Text>
          </Group>
        </Center>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <Center h={400}>
          <Alert
            icon={<IconAlertCircle size={24} />}
            title="Access Denied"
            color="red"
            variant="filled"
            w={400}
          >
            {error}
          </Alert>
        </Center>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Group justify="space-between" mb="lg">
        <Title order={2} c="#03045e">
          Shared Meter Data
        </Title>
        {expiresAt && (
          <Badge
            color="blue"
            variant="light"
            size="lg"
            leftSection={<IconClock size={14} />}
          >
            Expires: {formatDate(expiresAt)}
          </Badge>
        )}
      </Group>

      {selectedMeter ? (
        <SharedMeterDetails
          meter={selectedMeter}
          token={token!}
          onBack={meters.length > 1 ? () => setSelectedMeter(null) : undefined}
        />
      ) : (
        <>
          <Text c="dimmed" mb="md">
            Select a meter to view detailed readings
          </Text>
          <Grid>
            {meters.map((meter) => (
              <Grid.Col key={meter.id} span={{ base: 12, sm: 6, md: 4 }}>
                <Card
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedMeter(meter)}
                >
                  <Group justify="space-between" mb="xs">
                    <Text fw={600} size="lg">
                      {meter.meterSerialNo}
                    </Text>
                    <IconDroplet size={24} color="#0077b6" />
                  </Group>

                  {meter.location && (
                    <Text size="sm" c="dimmed" mb="sm">
                      {meter.location}
                    </Text>
                  )}

                  <Space h="sm" />

                  {meter.latestReading ? (
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Total Volume
                        </Text>
                        <Text fw={600}>
                          {formatNumberWithCommas(meter.latestReading.totalVolume)} L
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Group gap={4}>
                          <IconBattery size={16} />
                          <Text size="sm" c="dimmed">
                            Battery
                          </Text>
                        </Group>
                        <Text fw={500}>{meter.latestReading.battery}%</Text>
                      </Group>
                      <Group justify="space-between">
                        <Group gap={4}>
                          <IconTemperature size={16} />
                          <Text size="sm" c="dimmed">
                            Temperature
                          </Text>
                        </Group>
                        <Text fw={500}>
                          {(meter.latestReading.temperature / 100).toFixed(1)}°C
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" ta="right" mt="xs">
                        Last update: {formatDate(meter.latestReading.deviceTime)}
                      </Text>
                    </Stack>
                  ) : (
                    <Text size="sm" c="dimmed" ta="center">
                      No readings available
                    </Text>
                  )}
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </>
      )}
    </PublicLayout>
  );
}
